import styled from '@emotion/styled';
import React, { Component } from 'react';
import { PrimaryButton, Modal } from 'utils';
import brandRoles from 'utils/brandRoles';
import {
  CountPill,
  FONT,
  Section,
  SectionHeader,
  SectionTitle,
  TextButton,
} from './ui';
import SingleUpdate from 'scenes/Project/v2/components/ProjectUpdates/SingleUpdate';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { ProjectsActions } from 'redux/actions';
import { projectsServices } from 'services';
import DeliveryStatus from './DeliveryStatus';

// Solange Mails unterwegs sind, wird der Stand regelmaessig nachgeladen.
const POLL_MS = 8000;
// Direkt nach dem Veroeffentlichen gibt es kurz noch keinen Stand (Job startet gerade).
const FRESH_UPDATE_SECONDS = 120;
// Wie auf der Projektseite (ProjectUpdates): 32 px Abstand zwischen Updates.
const UpdatesList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 32px;
`;

const UpdateEntry = styled.div`
  position: relative;

  .delete {
    position: absolute;
    top: 4px;
    right: 0;
    color: ${brandRoles.neutral500};
  }

  /* Platz fuer "Loeschen" neben dem Titel */
  & > div:first-of-type > div:first-of-type {
    padding-right: 72px;
  }
`;

const ConfirmBody = styled.div`
  padding: 8px 24px 0;
  font-family: ${FONT};

  .confirm-title {
    font-size: 18px;
    font-weight: 600;
    line-height: 28px;
    color: ${brandRoles.neutral800};
  }

  .confirm-text {
    margin-top: 8px;
    font-size: 14px;
    line-height: 21px;
    color: ${brandRoles.neutral700};
  }

  .confirm-actions {
    display: flex;
    gap: 12px;
    margin-top: 24px;
  }

  .confirm-actions button {
    flex: 1;
    min-width: 0;
  }
`;

class TimeLineTab extends Component {
  static propTypes = {
    updates: PropTypes.array.isRequired,
    deleteProjectUpdates: PropTypes.func.isRequired,
    projectId: PropTypes.any.isRequired,
  };
  state = {
    currentLimit: 3,
    showModal: false,
    selectedUpdateId: null,
    deliveryStatus: {},
  };

  componentDidMount() {
    this.loadDeliveryStatus();
  }

  componentDidUpdate(prevProps) {
    if (prevProps.updates !== this.props.updates) this.loadDeliveryStatus();
  }

  componentWillUnmount() {
    clearTimeout(this.pollTimer);
    this.unmounted = true;
  }

  loadDeliveryStatus = () => {
    clearTimeout(this.pollTimer);
    projectsServices
      .getUpdateDeliveryStatus(this.props.projectId)
      .then((list) => {
        if (this.unmounted || !Array.isArray(list)) return;
        const deliveryStatus = {};
        list.forEach((entry) => {
          deliveryStatus[entry.update_id] = entry;
        });
        this.setState({ deliveryStatus }, this.schedulePoll);
      })
      .catch(() => {});
  };

  schedulePoll = () => {
    const { deliveryStatus } = this.state;
    const now = Date.now() / 1000;
    const running = Object.values(deliveryStatus).some((s) => s.pending > 0);
    const waitingForStart = (this.props.updates || []).some(
      (u) => !deliveryStatus[u.id] && now - u.date < FRESH_UPDATE_SECONDS
    );
    if (running || waitingForStart) {
      this.pollTimer = setTimeout(this.loadDeliveryStatus, POLL_MS);
    }
  };

  resendUpdate = (updateId) =>
    projectsServices.resendProjectUpdate(updateId).then(() => {
      this.loadDeliveryStatus();
    });

  handelCloseModal = () => {
    this.setState({ showModal: false, selectedUpdateId: null });
  };

  selectUpdateToDelete = (id) => {
    this.setState({ showModal: true, selectedUpdateId: id });
  };

  deleteUpdate = () => {
    this.props.deleteProjectUpdates(
      this.props.projectId,
      this.state.selectedUpdateId
    );
    this.handelCloseModal();
  };

  render() {
    const { updates } = this.props;

    return (
      <Section>
        <SectionHeader>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <SectionTitle>Updates</SectionTitle>
            <CountPill>{updates.length}</CountPill>
          </div>
        </SectionHeader>
        <UpdatesList>
          {updates.slice(0, this.state.currentLimit).map((update, inx) => (
            <UpdateEntry key={update.id || inx}>
              <SingleUpdate
                title={update.title}
                time={update.date}
                image={update.image}
                images={update.images || []}
                content={update.body || ''}
              />
              <TextButton
                type="button"
                className="delete"
                onClick={() => this.selectUpdateToDelete(update.id)}
              >
                Löschen
              </TextButton>
              <DeliveryStatus
                updateId={update.id}
                status={this.state.deliveryStatus[update.id]}
                onResend={this.resendUpdate}
              />
            </UpdateEntry>
          ))}
        </UpdatesList>
        {this.state.currentLimit < updates.length && (
          <div style={{ marginTop: 24 }}>
            <TextButton
              type="button"
              onClick={() =>
                this.setState({ currentLimit: this.state.currentLimit + 3 })
              }
            >
              Weitere Updates anzeigen
            </TextButton>
          </div>
        )}

        <Modal
          width="440px"
          noLogo
          showModal={this.state.showModal}
          handelCloseModal={this.handelCloseModal}
        >
          <ConfirmBody>
            <div className="confirm-title">Update löschen?</div>
            <div className="confirm-text">
              Das Update verschwindet von der Projektseite. E-Mails, die schon
              verschickt wurden, bleiben in den Postfächern.
            </div>
            <div className="confirm-actions">
              <PrimaryButton
                primary={true}
                type="button"
                onClick={() => this.handelCloseModal()}
              >
                Abbrechen
              </PrimaryButton>
              <PrimaryButton
                primary={false}
                type="button"
                onClick={() => this.deleteUpdate()}
              >
                Löschen
              </PrimaryButton>
            </div>
          </ConfirmBody>
        </Modal>
      </Section>
    );
  }
}

const mapDispatchToProps = (dispatch) => ({
  deleteProjectUpdates: (projectId, updateId) =>
    dispatch(ProjectsActions.deleteProjectUpdates(projectId, updateId)),
});

export default connect(null, mapDispatchToProps)(TimeLineTab);
